import {
  Component,
  ChangeDetectionStrategy,
  inject,
  signal,
  output,
  input,
  computed,
  effect,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  lucideFileText,
  lucideSettings,
  lucideEuro,
  lucideArrowRight,
  lucideArrowLeft,
  lucideCircleDot,
  lucideWrench,
  lucidePercent,
  lucideZap,
} from '@ng-icons/lucide';
import { BrnDialogImports } from '@spartan-ng/brain/dialog';
import { HlmDialogImports } from '@spartan-ng/helm/dialog';
import { HlmSeparatorDirective } from '@spartan-ng/helm/separator';
import { HlmSwitchComponent } from '@spartan-ng/helm/switch';
import { SuffixInput } from '@shared/components/suffix-input/suffix-input';
import { TextareaField } from '@shared/components/textarea-field/textarea-field';
import { SelectField } from '@shared/components/select-field/select-field';
import { WizardDialogHeader } from '@shared/components/wizard-dialog-header/wizard-dialog-header';
import { WizardDialogFooter } from '@shared/components/wizard-dialog-footer/wizard-dialog-footer';
import { StepLead } from '@shared/components/step-lead/step-lead';
import { FieldShell } from '@shared/components/field-shell/field-shell';
import {
  SummarySection,
  SummaryRow,
} from '@shared/components/summary-section/summary-section';
import {
  TimelineStep,
  createWizardSteps,
} from '@shared/models/step-timeline.model';
import { TemplateStore } from '@features/catalog/store/template.store';
import { TemplateService } from '@features/catalog/services/template.service';
import { EquipmentStore } from '@features/catalog/store/equipment.store';
import {
  CreateTemplateDto,
  UpdateTemplateDto,
  SolarTemplate,
  TemplateWithEquipment,
} from '@features/catalog/models/template.model';
import { ModuleSelector } from '@shared/components/module-selector/module-selector.component';
import { InverterSelector } from '@shared/components/inverter-selector/inverter-selector';
import { StorageSelector } from '@shared/components/storage-selector/storage-selector';
import { SolarModule } from '@features/catalog/models/module.model';
import { SolarInverter } from '@features/catalog/models/inverter.model';
import { SolarStorage } from '@features/catalog/models/storage.model';

/**
 * Multi-step dialog for creating new solar templates.
 * Uses the shared step-timeline component for progress visualization.
 *
 * Steps:
 * 1. Basic Info (name, description)
 * 2. Equipment Selection (module, inverter(s), storage(s))
 * 3. Lease Info (only for lease templates)
 * 4. Installation Costs
 * 5. Analysis Values
 * 6. Summary
 */
@Component({
  selector: 'app-create-template-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgIconComponent,
    ...BrnDialogImports,
    ...HlmDialogImports,
    HlmSeparatorDirective,
    HlmSwitchComponent,
    ModuleSelector,
    InverterSelector,
    StorageSelector,
    SuffixInput,
    TextareaField,
    SelectField,
    WizardDialogHeader,
    WizardDialogFooter,
    StepLead,
    FieldShell,
    SummarySection,
    SummaryRow,
  ],
  providers: [
    provideIcons({
      lucideFileText,
      lucideSettings,
      lucideEuro,
      lucideArrowRight,
      lucideArrowLeft,
      lucideCircleDot,
      lucideWrench,
      lucidePercent,
      lucideZap,
    }),
  ],
  templateUrl: './create-template-dialog.html',
  styleUrl: './create-template-dialog.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateTemplateDialog implements OnInit {
  private readonly fb = inject(UntypedFormBuilder);
  private readonly templateStore = inject(TemplateStore);
  private readonly templateService = inject(TemplateService);
  readonly equipmentStore = inject(EquipmentStore);

  // Inputs
  organizationId = input.required<string>();

  // Outputs
  templateCreated = output<SolarTemplate>();
  templateUpdated = output<SolarTemplate>();

  // Multi-step state (6 steps: Basic Info, Template Type, Lease Info (if applicable), Equipment, Costs, Analysis Values, Summary)
  currentStep = signal<1 | 2 | 3 | 4 | 5 | 6 | 7>(1);

  // Dialog state
  state = signal<'open' | 'closed'>('closed');
  editTemplate = signal<SolarTemplate | null>(null);

  isEditing = computed(() => !!this.editTemplate());

  computedTitle = computed(() =>
    this.isEditing() ? 'Vorlage bearbeiten' : 'Neue Vorlage erstellen',
  );

  computedDescription = computed(() =>
    this.isEditing()
      ? 'Bearbeiten Sie die Vorlage in 5 Schritten und speichern Sie die Änderungen.'
      : 'Erstellen Sie eine neue Vorlage für Ihre Solaranalysen in 5 einfachen Schritten',
  );

  computedSubmitButtonText = computed(() =>
    this.isEditing() ? 'Vorlage speichern' : 'Vorlage erstellen',
  );

  // Form state
  isSubmitting = signal(false);
  error = signal<string | null>(null);

  // Step 1: Basic Info
  basicInfoForm: UntypedFormGroup = this.fb.group({
    name: ['', [Validators.required, Validators.minLength(3)]],
    description: [''],
    templateType: ['buying', [Validators.required]], // 'buying' or 'lease'
  });

  /** Options for the Vorlagentyp dropdown. */
  readonly templateTypeOptions = [
    { value: 'buying', label: 'Kauf' },
    { value: 'lease', label: 'Miete' },
  ];

  // Track template type as a signal for reactive updates
  templateType = signal<'buying' | 'lease'>('buying');

  // Step 1b: Lease-specific fields (only if templateType === 'lease')
  leaseForm: UntypedFormGroup = this.fb.group({
    contractDurationPv: [null, [Validators.required, Validators.min(1)]],
    contractDurationStorage: [null, [Validators.min(1)]],
    foreignCapitalQuota: [
      null,
      [Validators.required, Validators.min(0), Validators.max(100)],
    ],
    insuranceCostsPerYear: [null, [Validators.required, Validators.min(0)]],
    maintenanceCostsPercentage: [
      null,
      [Validators.required, Validators.min(0)],
    ],
    zielgewinn: [null, [Validators.required, Validators.min(0)]],
  });

  // Step 2: Equipment Selection
  equipmentForm: UntypedFormGroup = this.fb.group({
    moduleId: ['', [Validators.required]],
    inverterIds: [[] as string[], [Validators.required]],
    storageIds: [[] as string[]],
  });

  // Step 3: Installation Costs
  costsForm: UntypedFormGroup = this.fb.group({
    dcElectricalMaterialCosts: [null, [Validators.required, Validators.min(0)]],
    acInstallationCosts: [null, [Validators.required, Validators.min(0)]],
    scaffoldingCosts: [null, [Validators.required, Validators.min(0)]],
    additionalRoofSurchargePerArea: [
      null,
      [Validators.required, Validators.min(0)],
    ],
    gridConnectionCosts: [null, [Validators.required, Validators.min(0)]],
    otherCosts: [null, [Validators.min(0)]],
    maintenanceCostsPerYear: [0, [Validators.min(0)]],
    feedInTariffUpTo10kwp: [0, [Validators.min(0)]],
    feedInTariffUpTo40kwp: [0, [Validators.min(0)]],
    wallbox: [false],
  });

  // Step 4: Analysis Values
  analysisValuesForm: UntypedFormGroup = this.fb.group({
    electricityPrice: [null, [Validators.required, Validators.min(0.01)]],
    electricityPriceIncrease: [null, [Validators.required, Validators.min(0)]],
    calculationPeriod: [null, [Validators.required, Validators.min(1)]],
  });

  // Wizard steps for the timeline
  private readonly stepLabels = [
    'Grunddaten',
    'Komponenten',
    'Kosten',
    'Analysewerte',
    'Zusammenfassung',
  ];

  /** One lead sentence per step, keyed to the same lease/purchase branching. */
  readonly stepLead = computed(() => {
    const leads = this.isLeaseTemplate()
      ? [
          'Name und Typ der Vorlage. Der Typ bestimmt die weiteren Schritte.',
          'Modul, Wechselrichter und Speicher, die diese Vorlage verwendet.',
          'Vertragslaufzeiten und Konditionen des Mietmodells.',
          'Kosten je Komponente und Pauschalen pro Anlage.',
          'Vorgaben für Simulation und Amortisationsrechnung.',
          'Prüfen Sie die Vorlage vor dem Speichern.',
        ]
      : [
          'Name und Typ der Vorlage. Der Typ bestimmt die weiteren Schritte.',
          'Modul, Wechselrichter und Speicher, die diese Vorlage verwendet.',
          'Kosten je Komponente und Pauschalen pro Anlage.',
          'Vorgaben für Simulation und Amortisationsrechnung.',
          'Prüfen Sie die Vorlage vor dem Speichern.',
        ];
    return leads[this.currentStep() - 1] ?? '';
  });

  wizardSteps = computed<TimelineStep[]>(() => {
    let labels = ['Grunddaten', 'Komponenten'];

    if (this.isLeaseTemplate()) {
      labels.push('Miet-Informationen');
    }

    labels.push('Kosten', 'Analysewerte', 'Zusammenfassung');
    return createWizardSteps(labels, this.currentStep());
  });

  // Computed: Check if template type is lease
  isLeaseTemplate = computed(() => {
    return this.templateType() === 'lease';
  });

  // Signals for equipment selection binding
  selectedModuleId = signal<string>('');
  selectedModule = signal<SolarModule | null>(null);
  selectedInverterIds = signal<string[]>([]);
  selectedInverters = signal<SolarInverter[]>([]);
  selectedStorageIds = signal<string[]>([]);
  selectedStorages = signal<SolarStorage[]>([]);

  constructor() {
    // Legitimate side-effect: sync signal selections into the reactive form.
    // These are DOM/form side effects driven by signal changes, not state derivation.
    effect(() => {
      const moduleId = this.selectedModuleId();
      this.equipmentForm
        .get('moduleId')
        ?.setValue(moduleId, { emitEvent: false });
    });

    effect(() => {
      const inverterIds = this.selectedInverterIds();
      this.equipmentForm
        .get('inverterIds')
        ?.setValue(inverterIds, { emitEvent: false });
    });

    effect(() => {
      const storageIds = this.selectedStorageIds();
      this.equipmentForm
        .get('storageIds')
        ?.setValue(storageIds, { emitEvent: false });
    });
  }

  ngOnInit(): void {
    // Load equipment for the selectors. The selectors are now presentational and
    // receive data via input(); this smart parent owns the data fetching.
    const orgId = this.organizationId();
    if (orgId) {
      this.equipmentStore.loadModules(orgId);
      this.equipmentStore.loadInverters(orgId);
      this.equipmentStore.loadStorages(orgId);
    }
  }

  // Step validation
  get canProceedFromStep1(): boolean {
    return this.basicInfoForm.valid;
  }

  get canProceedFromStep2Lease(): boolean {
    return this.leaseForm.valid;
  }

  get canProceedFromEquipmentStep(): boolean {
    return (
      this.selectedModuleId() !== '' &&
      this.selectedInverterIds().length >= 1 &&
      this.selectedInverterIds().length <= 5
    );
  }

  get canProceedFromStep3(): boolean {
    return this.costsForm.valid;
  }

  get canProceedFromStep4(): boolean {
    return this.analysisValuesForm.valid;
  }

  /**
   * Single gate for the shared footer. Step numbers shift by one once a lease
   * template inserts its extra step, so the mapping branches the same way the
   * step content does.
   */
  canProceedFromCurrentStep(): boolean {
    const step = this.currentStep();
    if (this.isLeaseTemplate()) {
      switch (step) {
        case 1: return this.canProceedFromStep1;
        case 2: return this.canProceedFromEquipmentStep;
        case 3: return this.canProceedFromStep2Lease;
        case 4: return this.canProceedFromStep3;
        case 5: return this.canProceedFromStep4;
        case 6: return this.canSubmit;
        default: return false;
      }
    }
    switch (step) {
      case 1: return this.canProceedFromStep1;
      case 2: return this.canProceedFromEquipmentStep;
      case 3: return this.canProceedFromStep3;
      case 4: return this.canProceedFromStep4;
      case 5: return this.canSubmit;
      default: return false;
    }
  }

  get canSubmit(): boolean {
    return (
      this.basicInfoForm.valid &&
      (!this.isLeaseTemplate() || this.leaseForm.valid) &&
      this.canProceedFromEquipmentStep &&
      this.costsForm.valid &&
      this.analysisValuesForm.valid
    );
  }

  // Module selection handler
  onModuleSelected(module: SolarModule): void {
    console.log('[CreateTemplateDialog] onModuleSelected called:', module);
    this.selectedModule.set(module);
    console.log('[CreateTemplateDialog] selectedModule signal set');
    this.selectedModuleId.set(module.id);
    console.log(
      '[CreateTemplateDialog] selectedModuleId signal set:',
      module.id,
    );
    this.equipmentForm.get('moduleId')?.setValue(module.id);
    console.log('[CreateTemplateDialog] equipment form moduleId updated');
  }

  // Inverters selection handler
  onInvertersSelected(inverters: SolarInverter[]): void {
    console.log(
      '[CreateTemplateDialog] onInvertersSelected called:',
      inverters,
    );
    const ids = inverters.map((inv) => inv.id);
    this.selectedInverters.set(inverters);
    console.log('[CreateTemplateDialog] selectedInverters signal set');
    this.selectedInverterIds.set(ids);
    console.log('[CreateTemplateDialog] selectedInverterIds signal set:', ids);
    this.equipmentForm.get('inverterIds')?.setValue(ids);
    console.log('[CreateTemplateDialog] equipment form inverterIds updated');
  }

  // Storages selection handler
  onStoragesSelected(storages: SolarStorage[]): void {
    console.log('[CreateTemplateDialog] onStoragesSelected called:', storages);
    const ids = storages.map((storage) => storage.id);
    this.selectedStorages.set(storages);
    console.log('[CreateTemplateDialog] selectedStorages signal set');
    this.selectedStorageIds.set(ids);
    console.log('[CreateTemplateDialog] selectedStorageIds signal set:', ids);
    this.equipmentForm.get('storageIds')?.setValue(ids);
    console.log('[CreateTemplateDialog] equipment form storageIds updated');
  }

  public open(template: SolarTemplate | null = null): void {
    this.resetDialogState();
    if (template) {
      this.editTemplate.set(template);
      void this.loadTemplateForEdit(template.id);
    }

    this.state.set('open');
  }

  public close(): void {
    this.state.set('closed');
    this.resetDialog();
  }

  // Step navigation
  nextStep(): void {
    const maxStep = this.isLeaseTemplate() ? 7 : 6;
    if (this.currentStep() < maxStep) {
      this.currentStep.set(
        (this.currentStep() + 1) as 1 | 2 | 3 | 4 | 5 | 6 | 7,
      );
    }
  }

  prevStep(): void {
    if (this.currentStep() > 1) {
      this.currentStep.set(
        (this.currentStep() - 1) as 1 | 2 | 3 | 4 | 5 | 6 | 7,
      );
    }
  }

  /**
   * Jump to an already-reached step. Used by the summary step's "Ändern" links.
   */
  jumpToStep(target: number): void {
    if (target > 0 && target <= this.currentStep()) {
      this.currentStep.set(target as 1 | 2 | 3 | 4 | 5 | 6 | 7);
    }
  }

  // Reset dialog state
  resetDialog(): void {
    this.editTemplate.set(null);
    this.resetDialogState();
  }

  private resetDialogState(): void {
    this.currentStep.set(1);
    this.setTemplateType('buying');
    this.basicInfoForm.reset({
      name: '',
      description: '',
      templateType: 'buying',
    });
    this.leaseForm.reset({
      contractDurationPv: null,
      contractDurationStorage: null,
      foreignCapitalQuota: null,
      insuranceCostsPerYear: null,
      maintenanceCostsPercentage: null,
      zielgewinn: null,
    });
    this.equipmentForm.reset({
      moduleId: '',
      inverterIds: [],
      storageIds: [],
    });
    this.costsForm.reset({
      dcElectricalMaterialCosts: null,
      acInstallationCosts: null,
      scaffoldingCosts: null,
      additionalRoofSurchargePerArea: null,
      gridConnectionCosts: null,
      otherCosts: null,
      maintenanceCostsPerYear: 0,
      feedInTariffUpTo10kwp: 0,
      feedInTariffUpTo40kwp: 0,
      wallbox: false,
    });
    this.analysisValuesForm.reset({
      electricityPrice: null,
      electricityPriceIncrease: null,
      calculationPeriod: null,
    });
    this.selectedModuleId.set('');
    this.selectedModule.set(null);
    this.selectedInverterIds.set([]);
    this.selectedInverters.set([]);
    this.selectedStorageIds.set([]);
    this.selectedStorages.set([]);
    this.error.set(null);
  }

  private populateForm(template: SolarTemplate): void {
    const templateType = template.type === 'lease' ? 'lease' : 'buying';
    const moduleId =
      template.moduleId ?? (template as TemplateWithEquipment).module?.id ?? '';
    const inverterIds =
      template.inverterIds ??
      (template as TemplateWithEquipment).inverters?.map(
        (inverter) => inverter.id,
      ) ??
      [];
    const storageIds =
      template.storageIds ??
      (template as TemplateWithEquipment).storages?.map(
        (storage) => storage.id,
      ) ??
      [];

    this.basicInfoForm.patchValue({
      name: template.name,
      description: template.description ?? '',
      templateType,
    });
    this.setTemplateType(templateType);

    const maintenanceCostsPerYear =
      template.hubMetadata?.maintenanceCostsPerYear ??
      template.maintenanceCostsPerYear ??
      0;

    this.leaseForm.patchValue({
      contractDurationPv: template.pvContractDuration ?? null,
      contractDurationStorage: template.storageContractDuration ?? null,
      foreignCapitalQuota: template.debtCapitalRatio ?? null,
      insuranceCostsPerYear: template.insuranceCostsPerYear ?? null,
      maintenanceCostsPercentage: template.maintenanceCostsPerYear ?? null,
      zielgewinn: template.targetProfit ?? null,
    });

    this.costsForm.patchValue({
      dcElectricalMaterialCosts: template.dcElectricalMaterialCosts ?? null,
      acInstallationCosts: template.acInstallationCosts ?? null,
      scaffoldingCosts: template.scaffoldingCosts ?? null,
      additionalRoofSurchargePerArea:
        template.additionalRoofSurchargePerArea ?? null,
      gridConnectionCosts: template.gridConnectionCosts ?? null,
      otherCosts: template.otherCosts ?? null,
      maintenanceCostsPerYear,
      feedInTariffUpTo10kwp: template.hubMetadata?.feedInTariffUpTo10kwp ?? 0,
      feedInTariffUpTo40kwp: template.hubMetadata?.feedInTariffUpTo40kwp ?? 0,
      wallbox: template.hubMetadata?.wallbox ?? false,
    });

    this.analysisValuesForm.patchValue({
      electricityPrice: template.electricityPrice,
      electricityPriceIncrease: template.electricityPriceIncrease,
      calculationPeriod: template.calculationPeriod,
    });

    this.selectedModuleId.set(moduleId);
    this.selectedInverterIds.set(inverterIds);
    this.selectedStorageIds.set(storageIds);
    this.selectedModule.set(null);
    this.selectedInverters.set([]);
    this.selectedStorages.set([]);
  }

  private async loadTemplateForEdit(templateId: string): Promise<void> {
    try {
      const organizationId = this.organizationId();
      const fullTemplate = await this.templateService.getWithEquipment(
        organizationId,
        templateId,
      );

      if (this.editTemplate()?.id !== templateId) {
        return;
      }

      this.populateForm(fullTemplate as TemplateWithEquipment);
    } catch (error) {
      console.error(
        '[CreateTemplateDialog] Error loading template for edit:',
        error,
      );
      this.error.set('Vorlage konnte nicht geladen werden');
    }
  }

  setTemplateType(type: 'buying' | 'lease'): void {
    this.templateType.set(type);
    this.basicInfoForm
      .get('templateType')
      ?.setValue(type, { emitEvent: false });

    const maxStep = type === 'lease' ? 7 : 6;
    if (this.currentStep() > maxStep) {
      this.currentStep.set(maxStep as 1 | 2 | 3 | 4 | 5 | 6 | 7);
    }
  }

  // Submit template
  async onSubmit(dialogContext: any): Promise<void> {
    if (!this.canSubmit || this.isSubmitting()) return;

    this.isSubmitting.set(true);
    this.error.set(null);

    try {
      const basicInfo = this.basicInfoForm.value;
      const equipment = this.equipmentForm.value;
      const costs = this.costsForm.value;
      const analysisValues = this.analysisValuesForm.value;
      const lease = this.leaseForm.value;
      const maintenanceCostsPerYear = this.isLeaseTemplate()
        ? lease.maintenanceCostsPercentage
        : costs.maintenanceCostsPerYear;

      const editingTemplate = this.editTemplate();

      if (editingTemplate) {
        const request: UpdateTemplateDto = {
          name: basicInfo.name!,
          description: basicInfo.description ?? undefined,
          dcElectricalMaterialCosts:
            costs.dcElectricalMaterialCosts ?? undefined,
          acInstallationCosts: costs.acInstallationCosts ?? undefined,
          scaffoldingCosts: costs.scaffoldingCosts ?? undefined,
          additionalRoofSurchargePerArea:
            costs.additionalRoofSurchargePerArea ?? undefined,
          gridConnectionCosts: costs.gridConnectionCosts ?? undefined,
          otherCosts: costs.otherCosts ?? undefined,
          electricityPrice: analysisValues.electricityPrice!,
          electricityPriceIncrease: analysisValues.electricityPriceIncrease!,
          calculationPeriod: analysisValues.calculationPeriod!,
          pvContractDuration: lease.contractDurationPv ?? undefined,
          storageContractDuration: lease.contractDurationStorage ?? undefined,
          debtCapitalRatio: lease.foreignCapitalQuota ?? undefined,
          insuranceCostsPerYear: lease.insuranceCostsPerYear ?? undefined,
          maintenanceCostsPerYear: maintenanceCostsPerYear ?? undefined,
          targetProfit: lease.zielgewinn ?? undefined,
          moduleId: equipment.moduleId ?? undefined,
          inverterIds: equipment.inverterIds ?? undefined,
          storageIds: equipment.storageIds ?? undefined,
          hubMetadata: {
            feedInTariffUpTo10kwp: costs.feedInTariffUpTo10kwp ?? 0,
            feedInTariffUpTo40kwp: costs.feedInTariffUpTo40kwp ?? 0,
            wallbox: costs.wallbox ?? false,
            maintenanceCostsPerYear: maintenanceCostsPerYear ?? 0,
          },
        };

        const updatedTemplate = await this.templateStore.updateTemplate(
          this.organizationId(),
          editingTemplate.id,
          request,
        );

        if (updatedTemplate) {
          this.templateUpdated.emit(updatedTemplate);
          this.close();
        } else {
          throw new Error('Failed to update template');
        }
      } else {
        const request: CreateTemplateDto = {
          name: basicInfo.name!,
          description: basicInfo.description ?? undefined,
          type: basicInfo.templateType === 'lease' ? 'lease' : 'purchase',
          module: equipment.moduleId ? { id: equipment.moduleId } : undefined,
          inverters: (equipment.inverterIds ?? []).map((id: string) => ({
            id,
          })),
          storages: (equipment.storageIds ?? []).map((id: string) => ({ id })),
          dcElectricMaterial: costs.dcElectricalMaterialCosts ?? undefined,
          acInstallationCosts: costs.acInstallationCosts ?? undefined,
          scaffoldingCosts: costs.scaffoldingCosts ?? undefined,
          additionalRoofSurcharge:
            costs.additionalRoofSurchargePerArea ?? undefined,
          gridConnectionCosts: costs.gridConnectionCosts ?? undefined,
          otherCosts: costs.otherCosts ?? 0,
          electricityPrice: analysisValues.electricityPrice!,
          electricityPriceIncrease: analysisValues.electricityPriceIncrease!,
          calculationPeriod: analysisValues.calculationPeriod!,
          pvContractDuration: lease.contractDurationPv ?? undefined,
          storageContractDuration: lease.contractDurationStorage ?? undefined,
          debtCapitalRatio: lease.foreignCapitalQuota ?? undefined,
          insuranceCostsPerYear: lease.insuranceCostsPerYear ?? undefined,
          maintenanceCostsPerYear: maintenanceCostsPerYear ?? undefined,
          targetProfit: lease.zielgewinn ?? undefined,
          hubMetadata: {
            feedInTariffUpTo10kwp: costs.feedInTariffUpTo10kwp ?? 0,
            feedInTariffUpTo40kwp: costs.feedInTariffUpTo40kwp ?? 0,
            wallbox: costs.wallbox ?? false,
            maintenanceCostsPerYear: maintenanceCostsPerYear ?? 0,
          },
        };

        const createdTemplate = await this.templateStore.createTemplate(
          this.organizationId(),
          request,
        );

        if (createdTemplate) {
          this.templateCreated.emit(createdTemplate);
          this.close();
        } else {
          throw new Error('Failed to create template');
        }
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : this.isEditing()
            ? 'Fehler beim Aktualisieren der Vorlage'
            : 'Fehler beim Erstellen der Vorlage';
      this.error.set(errorMessage);
    } finally {
      this.isSubmitting.set(false);
    }
  }
}
